import axios from 'axios'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Preloader from '../../components/Preloader/Preloader'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { FaSpinner } from 'react-icons/fa'
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Helmet } from 'react-helmet'

const ResetPassword = () => {
    const navigate = useNavigate()
    const [showResetPassword, setShowResetPassword] = useState(false)
    const [loading, setLoading] = useState(false)
    const { t } = useTranslation()
    const [timeLeft, setTimeLeft] = useState(0)

    // Function to start the timer and store timestamp
    const startTimer = () => {
        const now = Date.now()
        const expireTime = now + 60 * 1000 // 1-minute expiry
        localStorage.setItem("otp_timer", expireTime.toString())
        setTimeLeft(60)
    }

    // Effect to check the timer on mount
    useEffect(() => {
        const storedExpireTime = localStorage.getItem("otp_timer")
        if (storedExpireTime) {
            const remainingTime = Math.floor((parseInt(storedExpireTime) - Date.now()) / 1000)
            if (remainingTime > 0) {
                setTimeLeft(remainingTime)
            }
        }

        // Timer countdown
        const interval = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(interval)
                    localStorage.removeItem("otp_timer")
                    return 0
                }
                return prev - 1
            });
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    
    const generateOtp = async (values:any) => {
        if (values.email.trim() === "") return toast.error(t('register.email_required'))

        setLoading(true)
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/generate-otp`, {
            "email": values.email,
            "reset_password": true,
        }).then( res => {
            setLoading(false)
            console.log("res", res)
            if (res.status == 200) {
                setShowResetPassword(true)
            }
            startTimer() // Start timer after OTP request
        }).catch( err => {
            setLoading(false)
            console.log("err", err.response)
            
            if (err.response && err.response.data ) {
                for (let msgKey in err.response.data) {
                    toast.error(err.response.data[msgKey][0])
                }
            } else {
                toast.error(t('reset_password.error_sending_otp'))
            }
        })
    }

    const resetPassword = async (values:any) => {
        if (values.email.trim() === "" || values.otp.trim() === "") return toast.error(t('register.email_otp_required'))

        setLoading(true)
        axios.post(`${process.env.REACT_APP_API_BASE_URL}/user/reset-password`, {
            "email": values.email.trim(),
            "otp": values.otp.trim() ,
            "new_password": values.password,
            "confirm_new_password": values.confirmPassword,
        }).then( res => {
            setLoading(false)
            console.log("res", res)
            toast.success("Password reset succesfully")
            navigate("/login")

        }).catch( err => {
            setLoading(false)
            console.log("err", err.response)
            toast.error(err.response.data ? err.response.data.error : "Error Resetting")
        })
    }

    const validationSchema = (showResetPassword: boolean) => 
        Yup.object().shape({
            email: Yup.string()
                .email(t("validation.invalid_email"))
                .required(t("validation.required")),
    
            otp: showResetPassword 
                ? Yup.string().required(t("validation.required"))
                : Yup.string(),
    
            password: showResetPassword
                ? Yup.string().min(6, t("validation.password_min")).required(t("validation.required"))
                : Yup.string(),
    
            confirmPassword: showResetPassword
                ? Yup.string()
                    .oneOf([Yup.ref("password")], t("validation.password_match"))
                    .required(t("validation.required"))
                : Yup.string(),
        })

    return (
        <>
        <Helmet>
            <title>Sign Up for MyKoku | Free Smart School Platform Malaysia</title>
            <meta name="description" content="Create your free MyKoku account today. Used by Malaysian schools to manage attendance, learning & payment. SK, SMK, SJKC & SMJK supported." />
            <meta name="robots" content="noindex, nofollow" />
        </Helmet>

        {loading && <Preloader />}

        <div className="pt-4 px-4 pb-12">
            <div className="mb-6">
                <h3 className="text-2xl font-bold text-black">{t('reset_password.reset_password')}</h3>
            </div>

            <div>
                <Formik
                    initialValues={{ email: "", otp: "", password: "", confirmPassword: "" }}
                    validationSchema={validationSchema(showResetPassword)}
                    onSubmit={(values) => {
                        showResetPassword ? resetPassword(values) : generateOtp(values);
                    }}
                >
                    {({ isSubmitting }) => (
                    <Form className="px-2 py-4 bg-white rounded-3xl">
                        <div className="mb-7">
                            <label className="text-sm font-medium text-black block mb-2">{t("reset_password.email")}</label>
                            <Field
                                type="text"
                                name="email"
                                placeholder={t("reset_password.enter_email")}
                                className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                            />
                            <ErrorMessage name="email" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>

                        {showResetPassword && (
                        <>
                            <div className="mb-7">
                                <label className="text-sm font-medium text-black block mb-2">{t("reset_password.otp")}</label>
                                <Field
                                    type="text"
                                    name="otp"
                                    placeholder={t("reset_password.enter_otp")}
                                    className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                                />
                                <ErrorMessage name="otp" component="div" className="mt-1 text-red-500 text-sm" />
                            </div>

                            <div className="mb-7">
                                <label className="text-sm font-medium text-black block mb-2">{t("reset_password.password")}</label>
                                <Field
                                    type="password"
                                    name="password"
                                    placeholder={t("reset_password.enter_password")}
                                    className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                                />
                                <ErrorMessage name="password" component="div" className="mt-1 text-red-500 text-sm" />
                            </div>

                            <div className="mb-7">
                                <label className="text-sm font-medium text-black block mb-2">{t("reset_password.confirm_password")}</label>
                                <Field
                                    type="password"
                                    name="confirmPassword"
                                    placeholder={t("reset_password.confirm_password")}
                                    className="w-full text-lg text-black border-b-2 border-gray-300 focus:border-theme outline-none py-2"
                                />
                                <ErrorMessage name="confirmPassword" component="div" className="mt-1 text-red-500 text-sm" />
                            </div>
                        </>
                        )}

                        <div className="mb-7 text-sm text-gray-500">
                            {t("reset_password.login")}{" "}
                            <Link to="/login" className="text-theme">{t("reset_password.sign_in")}</Link>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={(!showResetPassword && loading) || (!showResetPassword && timeLeft > 0)}
                            className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center disabled:bg-gray-300"
                        >
                        {loading ? (
                            <FaSpinner className="animate-spin" />
                        ) : showResetPassword ? (
                            t('reset_password.reset')
                        ) : timeLeft > 0 ? (
                            `Resend OTP (${timeLeft}s)`
                        ) : (
                            t("reset_password.send_otp")
                        )}
                        </button>

                        <div className="grid w-1/2 mt-4 px-2 float-right">
                            <select className="px-2 py-1 border rounded-full text-md bg-white focus:outline-none focus:ring-2 focus:ring-theme appearance-none" onChange={(e) => { i18next.changeLanguage(e.target.value) }}>
                                <option value="-1" selected disabled>-- Language --</option>
                                <option value="en">English</option>
                                <option value="zh">Mandarin</option>
                                <option value="ms">Bahasa</option>
                            </select>
                        </div>
                    </Form>
                    )}
                </Formik>
            </div>
        </div>
        </>
 
    )
}

export default ResetPassword